import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CreditCard, Trophy, Calendar, Tag, Users, Settings, LogOut, Scan, Store, Shield, FileText, Bell, ChevronRight, ClipboardCheck } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import ThemeToggle from "@/components/ThemeToggle";

export default function Dashboard({ user, logout }) {
  // An account can carry its privilege either on `role` or in the `roles` array,
  // so check both or an admin loses their tiles.
  const held = [user.role, ...(user.roles || [])].filter(Boolean);
  const holds = (...wanted) => held.some((r) => wanted.includes(r));
  const isAdmin = holds("convenor", "captain", "admin");
  const isMerchant = holds("convenor", "captain", "admin", "merchant");
  const isCommittee = holds("convenor", "captain", "admin", "committee_member", "secretary",
    "treasurer", "jsm_representative", "event_manager");
  const isEventManager = holds("convenor", "captain", "admin", "event_manager");
  const isStaff = holds("staff", "convenor", "captain", "admin");
  
  // Check team_roles for team management positions
  const teamManagementRoles = ["captain", "team_captain", "vice_captain", "manager", "team_admin"];
  const hasTeamManagementRole = (user.team_roles || []).some(tr => teamManagementRoles.includes(tr.role));
  const isTeamCaptain = ["team_captain", "vice_captain"].includes(user.role) || hasTeamManagementRole;
  
  const isTreasurer = holds("convenor", "captain", "admin", "treasurer");
  const isSecretary = holds("convenor", "captain", "admin", "secretary");
  const isAcademyAdmin = holds("academy_admin");
  
  // Check if user is academy-only (no section membership)
  const isSectionMember = user.member_type === "section" || (isAdmin && !isAcademyAdmin);
  const isSccMember = user.is_scc_member || isSectionMember;
  const isAcademyPlayer = user.roles && user.roles.includes("academy_player");
  const isAcademyParentOnly = user.roles && user.roles.includes("academy_parent") && !isSectionMember && !isAcademyPlayer;
  const isAcademyCoachOnly = user.roles && user.roles.includes("academy_coach") && !isSectionMember;
  const isAcademyOnly = isAcademyParentOnly || isAcademyCoachOnly || isAcademyAdmin;
  const showSectionFeatures = !isAcademyOnly;
  const showSccLogo = isSccMember;

  // Dashboard card component with premium styling and animations
  const DashboardCard = ({ to, icon: Icon, iconBg, title, description, badge, testId }) => (
    <Link to={to} data-testid={testId}>
      <Card className="group relative overflow-hidden bg-slate-900/50 border border-white/5 rounded-xl p-6 hover:border-yellow-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-yellow-500/10 cursor-pointer h-full card-shine">
        {/* Subtle gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/0 to-yellow-500/0 group-hover:from-slate-900/5 group-hover:to-transparent transition-all duration-500 rounded-xl pointer-events-none" />
        
        <div className={`force-dark relative flex items-center justify-center w-12 h-12 ${iconBg} rounded-xl mb-4 transition-transform duration-300 icon-scale`}>
          <Icon className="w-6 h-6 text-white icon-bounce" />
        </div>
        <h3 className="relative text-lg font-semibold text-white mb-1 group-hover:text-yellow-500 transition-colors duration-300">{title}</h3>
        <p className="relative text-slate-400 text-sm group-hover:text-slate-300 transition-colors duration-300">{description}</p>
        {badge && (
          <span className="absolute top-4 right-4 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-full font-medium badge-animate">
            {badge}
          </span>
        )}
        <ChevronRight className="absolute bottom-6 right-6 w-5 h-5 text-slate-600 group-hover:text-yellow-500 transition-all duration-300 chevron-animate" />
      </Card>
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <img
            src="/branding/football-and-more-white.png"
            alt="Football And More"
            className="h-9 md:h-11 w-auto"
            data-testid="dashboard-header"
          />
          <div className="flex items-center gap-3">
            <span className="text-slate-300 text-sm hidden sm:inline" data-testid="user-name">
              Welcome, <span className="text-white font-medium">{user.name}</span>
            </span>
            <ThemeToggle />
            <NotificationBell user={user} />
            <Button
              onClick={logout}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white hover:bg-white/5"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 md:py-12">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2 font-display" data-testid="dashboard-welcome">
            Dashboard
          </h2>
          <p className="text-slate-400">Your Football And More membership portal</p>
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Profile */}
          <DashboardCard
            to="/profile"
            icon={Users}
            iconBg="bg-gradient-to-br from-slate-900 to-amber-600"
            title="My Profile"
            description="Update your personal information"
            testId="profile-link"
          />

          {/* Notifications */}
          <DashboardCard
            to="/notification-settings"
            icon={Bell}
            iconBg="bg-gradient-to-br from-slate-900 to-indigo-600"
            title="Notifications"
            description="Email & Telegram alerts"
            testId="notification-settings-link"
          />

          {/* Membership Card - Only for SCC Club Members */}
          {showSccLogo && (
            <DashboardCard
              to="/member-card"
              icon={CreditCard}
              iconBg="bg-gradient-to-br from-slate-900 to-amber-600"
              title="Membership Card"
              description="View your digital membership card"
              testId="member-card-link"
            />
          )}

          {/* Teams */}
          {showSectionFeatures && (
            <DashboardCard
              to="/teams"
              icon={Users}
              iconBg="bg-gradient-to-br from-slate-900 to-amber-600"
              title="Teams"
              description="Join and manage your teams"
              testId="teams-link"
            />
          )}

          {/* Games */}
          <DashboardCard
            to="/games"
            icon={Trophy}
            iconBg="bg-gradient-to-br from-slate-900 to-amber-600"
            title="Games"
            description={isAcademyParentOnly ? "View your children's game schedules" : "View schedules and sign up"}
            testId="games-link"
          />

          {/* Events */}
          {showSectionFeatures && (
            <DashboardCard
              to="/events"
              icon={Calendar}
              iconBg="bg-gradient-to-br from-purple-500 to-violet-600"
              title="Events"
              description="Social events and gatherings"
              testId="events-link"
            />
          )}

          {/* Calendar */}
          <DashboardCard
            to="/game-calendar"
            icon={Calendar}
            iconBg="bg-gradient-to-br from-orange-500 to-red-500"
            title="Calendar View"
            description="See all games & events in calendar"
            testId="calendar-link"
          />

          {/* Match Reports */}
          <DashboardCard
            to="/match-reports"
            icon={FileText}
            iconBg="bg-gradient-to-br from-emerald-500 to-green-600"
            title="Match Reports"
            description="Read, rate & discuss game reports"
            testId="match-reports-link"
          />

          {/* Academy */}
          {(user.roles && (user.roles.includes("academy_parent") || user.roles.includes("academy_player") || user.roles.includes("academy_coach"))) || isAdmin ? (
            <DashboardCard
              to="/academy"
              icon={Shield}
              iconBg="bg-gradient-to-br from-slate-900 to-indigo-600"
              title="Academy"
              description="Youth development programs"
              testId="academy-link"
            />
          ) : (
            <DashboardCard
              to="/academy/start-registration"
              icon={Shield}
              iconBg="bg-gradient-to-br from-slate-900 to-indigo-600"
              title="Academy"
              description="Register your child"
              badge="Enroll Now"
              testId="academy-register-link"
            />
          )}

          {/* Coach for the Academy — in-portal, so a member never has to sign
              out and retype their email into the public form. */}
          {!holds("academy_coach") && (
            <DashboardCard
              to="/coach-application"
              icon={ClipboardCheck}
              iconBg="bg-gradient-to-br from-slate-900 to-yellow-600"
              title="Coach for the Academy"
              description="Apply to coach — keeps your current login"
              testId="coach-application-link"
            />
          )}
          <DashboardCard
            to="/discounts"
            icon={Tag}
            iconBg="bg-gradient-to-br from-slate-900 to-amber-600"
            title="Discounts"
            description="Exclusive member discounts"
            testId="discounts-link"
          />

          {/* Become a Merchant */}
          {!isMerchant && (
            <DashboardCard
              to="/merchant-application"
              icon={Store}
              iconBg="bg-gradient-to-br from-amber-500 to-orange-600"
              title="Become a Merchant"
              description="Apply to offer member discounts"
              testId="merchant-application-link"
            />
          )}

          {/* Merchant Portal & Scanner */}
          {isMerchant && (
            <>
              <DashboardCard
                to="/merchants"
                icon={Store}
                iconBg="bg-gradient-to-br from-slate-900 to-amber-600"
                title="Merchant Portal"
                description="Manage business & discounts"
                testId="merchants-link"
              />
              <DashboardCard
                to="/merchant-scanner"
                icon={Scan}
                iconBg="bg-gradient-to-br from-slate-900 to-amber-600"
                title="Merchant Scanner"
                description="Scan member discount QR codes"
                testId="scanner-link"
              />
            </>
          )}

          {/* Committee Availability */}
          {isCommittee && (
            <DashboardCard
              to="/committee-availability"
              icon={Calendar}
              iconBg="bg-gradient-to-br from-slate-900 to-amber-600"
              title="Committee Availability"
              description="View and manage committee calendar"
              testId="committee-availability-link"
            />
          )}

          {/* Committee Portal */}
          {isCommittee && (
            <DashboardCard
              to="/committee"
              icon={Shield}
              iconBg="bg-gradient-to-br from-purple-500 to-violet-600"
              title="Committee Portal"
              description="Manage members & schedule meetings"
              testId="committee-portal-link"
            />
          )}

          {/* Meeting Minutes - AI-powered */}
          {isCommittee && (
            <DashboardCard
              to="/meeting-minutes"
              icon={FileText}
              iconBg="bg-gradient-to-br from-emerald-500 to-teal-600"
              title="Meeting Minutes"
              description="AI transcription & action tracking"
              badge="AI"
              testId="meeting-minutes-link"
            />
          )}

          {/* Admin Panel */}
          {isAdmin && (
            <Link to="/admin" data-testid="admin-link">
              <Card className="group relative overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-yellow-500/50 rounded-xl p-6 hover:border-yellow-500 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-yellow-500/20 cursor-pointer h-full card-shine">
                {/* Subtle gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900/0 to-yellow-500/0 group-hover:from-slate-900/10 group-hover:to-transparent transition-all duration-500 rounded-xl pointer-events-none" />
                
                <div className="relative flex items-center justify-center w-12 h-12 bg-yellow-500/100 rounded-xl mb-4 transition-transform duration-300 icon-scale">
                  <Settings className="w-6 h-6 text-black icon-bounce" />
                </div>
                <h3 className="relative text-lg font-semibold text-white mb-1 group-hover:text-yellow-400 transition-colors duration-300">Admin Panel</h3>
                <p className="relative text-slate-400 text-sm group-hover:text-slate-300 transition-colors duration-300">Users, Teams, Data & Permissions</p>
                <ChevronRight className="absolute bottom-6 right-6 w-5 h-5 text-yellow-500/50 group-hover:text-yellow-500 transition-all duration-300 chevron-animate" />
              </Card>
            </Link>
          )}
          
          {/* Staff Portal - for SCC Sports Staff */}
          {isStaff && (
            <Link to="/staff" data-testid="staff-portal-link">
              <Card className="group relative overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-purple-500/50 rounded-xl p-6 hover:border-purple-500 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-purple-500/20 cursor-pointer h-full card-shine">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900/0 to-purple-500/0 group-hover:from-slate-900/10 group-hover:to-transparent transition-all duration-500 rounded-xl pointer-events-none" />
                
                <div className="relative flex items-center justify-center w-12 h-12 bg-purple-500/100 rounded-xl mb-4 transition-transform duration-300 icon-scale">
                  <Shield className="w-6 h-6 text-white icon-bounce" />
                </div>
                <h3 className="relative text-lg font-semibold text-white mb-1 group-hover:text-purple-400 transition-colors duration-300">Staff Portal</h3>
                <p className="relative text-slate-400 text-sm group-hover:text-slate-300 transition-colors duration-300">Logistics & Field Management</p>
                <ChevronRight className="absolute bottom-6 right-6 w-5 h-5 text-purple-500/50 group-hover:text-purple-500 transition-all duration-300 chevron-animate" />
              </Card>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
